/**
 * <notify-me-form> — back-in-stock email capture (SEO/Ads-safe).
 *
 * Additive to the honest sold-out state: it never touches the disabled buy button,
 * the "Sold out" label, the product's availability, or the JSON-LD. It only captures
 * an email + product/variant context and POSTs it to a configurable endpoint
 * (Google Apps Script web app — same pattern as the Starlink lead form).
 *
 * Variant awareness is handled server-side: the wrapper in main-product.liquid carries
 * `data-dynamic-variant-content`, so the fetch-based variant picker re-renders this
 * element (with correct variant id/title + WhatsApp text) on every variant change.
 * That means each rendered instance already targets the right variant — this component
 * only owns the async submit + success/error UI.
 */
class NotifyMeForm extends HTMLElement {
  constructor() {
    super();
    this.mountedAt = Date.now();
    this.submitting = false;
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  connectedCallback() {
    this.endpoint = this.dataset.endpoint || '';
    this.form = this.querySelector('[data-nm-form]');
    this.emailInput = this.querySelector('[data-nm-email]');
    this.honeypot = this.querySelector('[data-nm-hp]');
    this.submitBtn = this.querySelector('[data-nm-submit]');
    this.submitLabel = this.querySelector('[data-nm-submit-label]');
    this.errorEl = this.querySelector('[data-nm-error]');
    this.successEl = this.querySelector('[data-nm-success]');

    this.strings = (window.theme && window.theme.strings && window.theme.strings.notifyMe) || {};

    this.buildWhatsAppLink();

    if (this.form) {
      this.form.addEventListener('submit', this.handleSubmit);
      // Clear the error as soon as the shopper starts fixing the email.
      if (this.emailInput) {
        this.emailInput.addEventListener('input', () => this.clearError());
      }
    }
  }

  disconnectedCallback() {
    if (this.form) this.form.removeEventListener('submit', this.handleSubmit);
  }

  /**
   * Assemble the pre-filled WhatsApp message in JS rather than Liquid.
   * Reading the title/variant from the hidden inputs gives the browser-decoded
   * text (so "&", "'", '"' in a product title render cleanly), then we
   * encodeURIComponent the whole message exactly once. The server-rendered href
   * is a plain wa.me/<number> fallback for the no-JS case.
   */
  buildWhatsAppLink() {
    const waBtn = this.querySelector('[data-nm-wa]');
    if (!waBtn) return;
    const number = (waBtn.dataset.waNumber || '').trim();
    if (!number) return;
    const prefix = (waBtn.dataset.waPrefix || '').trim();
    const title = (this.querySelector('[name=product_title]') || {}).value || '';
    const variant = (this.querySelector('[name=variant_title]') || {}).value || '';
    let msg = (prefix ? prefix + ' ' : '') + title;
    if (variant) msg += ' (' + variant + ')';
    waBtn.href = 'https://wa.me/' + number + '?text=' + encodeURIComponent(msg.trim());
  }

  static isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
  }

  showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
    if (this.emailInput) this.emailInput.setAttribute('aria-invalid', 'true');
  }

  clearError() {
    if (!this.errorEl) return;
    this.errorEl.textContent = '';
    this.errorEl.hidden = true;
    if (this.emailInput) this.emailInput.removeAttribute('aria-invalid');
  }

  setLoading(isLoading) {
    this.submitting = isLoading;
    if (this.submitBtn) {
      this.submitBtn.disabled = isLoading;
      this.submitBtn.classList.toggle('notify-me__submit--loading', isLoading);
      this.submitBtn.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }
  }

  showSuccess() {
    if (this.form) this.form.hidden = true;
    const whatsapp = this.querySelector('.notify-me__whatsapp');
    if (whatsapp) whatsapp.hidden = true;
    if (this.successEl) {
      this.successEl.hidden = false;
      // Move focus so screen readers announce the confirmation.
      this.successEl.focus();
    }
  }

  handleSubmit(event) {
    event.preventDefault();
    if (this.submitting) return;
    this.clearError();

    // Silent bot bounce — pretend success, send nothing.
    if (this.honeypot && this.honeypot.value.trim() !== '') {
      this.showSuccess();
      return;
    }

    // Dwell-time gate — real users take more than a second to reach submit.
    if (Date.now() - this.mountedAt < 1500) {
      this.showSuccess();
      return;
    }

    const email = this.emailInput ? this.emailInput.value.trim() : '';
    if (!email || !NotifyMeForm.isValidEmail(email)) {
      this.showError(this.strings.invalidEmail || 'Please enter a valid email address.');
      if (this.emailInput) this.emailInput.focus();
      return;
    }

    if (!this.endpoint) {
      // Misconfigured — don't leave the shopper hanging.
      this.showError(this.strings.genericError || 'Something went wrong. Please try again later.');
      return;
    }

    const formData = new FormData(this.form);
    this.setLoading(true);

    fetch(this.endpoint, {
      method: 'POST',
      mode: 'no-cors',
      body: formData,
    })
      .then(() => {
        this.showSuccess();
      })
      .catch(() => {
        this.showError(this.strings.genericError || 'Something went wrong. Please try again later.');
      })
      .finally(() => {
        this.setLoading(false);
      });
  }
}

customElements.define('notify-me-form', NotifyMeForm);
